/**
 * SKUGenerator Component
 * 
 * Provides UI for generating WooCommerce SKUs using prefix + brand + auto-increment.
 * Includes dropdowns populated from API and manual override capability.
 * 
 * Requirements: 3.3, 3.4, 3.5, 3.6
 */

import { useState, useEffect, useCallback } from 'react';
import { fetchSKUOptions, generateSKU, type SKUOptionsResponse, type GenerateSKUResponse } from '../api/sku';

export interface SKUGeneratorProps {
  /** Current SKU value */
  value: string;
  /** Called when SKU value changes */
  onChange: (sku: string) => void;
  /** Current prefix selection */
  prefix: string;
  /** Called when prefix changes */
  onPrefixChange: (prefix: string) => void;
  /** Current brand selection */
  brand: string;
  /** Called when brand changes */
  onBrandChange: (brand: string) => void;
  /** Whether the field is disabled */
  disabled?: boolean;
}

type LoadingState = 'idle' | 'loading' | 'error';

export function SKUGenerator({
  value,
  onChange,
  prefix,
  onPrefixChange,
  brand,
  onBrandChange,
  disabled = false,
}: SKUGeneratorProps) {
  const [options, setOptions] = useState<SKUOptionsResponse | null>(null);
  const [optionsLoadState, setOptionsLoadState] = useState<LoadingState>('idle');
  const [generateState, setGenerateState] = useState<LoadingState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [isManualOverride, setIsManualOverride] = useState(false);

  // Load SKU options on mount
  useEffect(() => {
    let cancelled = false;

    async function loadOptions() {
      setOptionsLoadState('loading');
      setError(null);

      try {
        const data = await fetchSKUOptions();
        if (!cancelled) {
          setOptions(data);
          setOptionsLoadState('idle');
        }
      } catch (err) {
        if (!cancelled) {
          setOptionsLoadState('error');
          setError('Failed to load SKU options');
        }
      }
    }

    loadOptions();

    return () => {
      cancelled = true;
    };
  }, []);

  // Handle generate button click
  const handleGenerate = useCallback(async () => {
    if (!prefix || !brand) {
      setError('Please select both prefix and brand');
      return;
    }

    setGenerateState('loading');
    setError(null);

    try {
      const result: GenerateSKUResponse = await generateSKU(prefix, brand);
      onChange(result.sku);
      setIsManualOverride(false);
      setGenerateState('idle');
    } catch (err) {
      setGenerateState('error');
      setError('Failed to generate SKU');
    }
  }, [prefix, brand, onChange]);

  // Handle manual SKU input
  const handleManualChange = (newValue: string) => {
    onChange(newValue);
    setIsManualOverride(true);
  };

  const isLoading = optionsLoadState === 'loading';
  const isGenerating = generateState === 'loading';
  const canGenerate = prefix && brand && !isGenerating && !disabled;

  return (
    <div className="sku-generator">
      {error && (
        <div className="sku-generator-error" role="alert">
          {error}
        </div>
      )}

      <div className="sku-generator-selectors">
        <div className="sku-generator-field">
          <label htmlFor="skuPrefix">Prefix</label>
          <select
            id="skuPrefix"
            value={prefix}
            onChange={(e) => onPrefixChange(e.target.value)}
            disabled={disabled || isLoading}
          >
            <option value="">Select prefix...</option>
            {options?.prefixes.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>

        <div className="sku-generator-field">
          <label htmlFor="skuBrand">Brand</label>
          <select
            id="skuBrand"
            value={brand}
            onChange={(e) => onBrandChange(e.target.value)}
            disabled={disabled || isLoading}
          >
            <option value="">Select brand...</option>
            {options?.brands.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          className="btn btn-secondary sku-generate-btn"
          onClick={handleGenerate}
          disabled={!canGenerate}
        >
          {isGenerating ? 'Generating...' : 'Generate'}
        </button>
      </div>

      <div className="sku-generator-result">
        <label htmlFor="woocommerceSku">
          WooCommerce SKU
          {isManualOverride && <span className="manual-override-badge">(Manual)</span>}
        </label>
        <input
          type="text"
          id="woocommerceSku"
          value={value}
          onChange={(e) => handleManualChange(e.target.value)}
          placeholder="Generated SKU will appear here"
          disabled={disabled}
        />
        <p className="sku-help-text">
          Select prefix and brand, then click Generate. You can also manually override the SKU.
        </p>
      </div>
    </div>
  );
}

export default SKUGenerator;
